import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  IDefaultResponsiveSupport,
  IGlobalState,
} from '../../common/interfaces/common.interface';
import WindowsXPDesktop from './windows-xp-desktop/windows-xp-desktop';
import { Context as GlobalContext } from '../../context/global.context';
import AboutMe from './content/about-me';
import { OS_RESUME_WINDOWS } from '../../common/general-utilities/os-utilities';
import EmploymentHistoryComponent from '../plain-resume/employment-history/employment-history.component';
import ProjectsComponent from '../plain-resume/projects/projects.component';
import EducationComponent from '../plain-resume/education/education.component';
import CertificationsComponent from '../plain-resume/certifications/certifications.component';
import ProfessionalSkillsComponent from '../plain-resume/professional-skills/professional-skills.component';
import WindowsXPSplash from '../../components/windows-xp-splash/windows-xp-splash.component';
import AiAssistant from './content/ai-assistant';
import { WindowItem } from '../../common/interfaces/os-resume-interface';
import ResumePDF from './content/resume-pdf';
import AIChatBot from './content/ai-chat-bot/ai-chat-bot';
import JSCompiler from './content/js-compiler/js-compiler';
import Share from './content/share';

interface IOSResume extends IDefaultResponsiveSupport {}
const OSResume: React.FC<IOSResume> = ({ responsiveMode }) => {
  const [loading, setLoading] = useState(true);
  const context = useContext(GlobalContext);
  const resumeData = (context.state as IGlobalState).resumeData;
  const availableWindowsModals = useMemo<Map<string, WindowItem>>(
    () =>
      new Map([
        [
          OS_RESUME_WINDOWS.ABOUT_ME,
          {
            id: OS_RESUME_WINDOWS.ABOUT_ME,
            title: OS_RESUME_WINDOWS.ABOUT_ME,
            Component: (
              <AboutMe
                aboutMe={resumeData?.aboutMe}
                responsiveMode={responsiveMode}
              />
            ),
            Icon: <span>&#129333;</span>,
          },
        ],
        [
          OS_RESUME_WINDOWS.SKILLS,
          {
            id: OS_RESUME_WINDOWS.SKILLS,
            title: OS_RESUME_WINDOWS.SKILLS,
            Component: (
              <ProfessionalSkillsComponent
                professionalSkills={resumeData?.professionalSkills}
                responsiveMode={responsiveMode}
                osMode={true}
              />
            ),
            Icon: <span>&#127919;</span>,
          },
        ],
        [
          OS_RESUME_WINDOWS.EXPERIENCE,
          {
            id: OS_RESUME_WINDOWS.EXPERIENCE,
            title: OS_RESUME_WINDOWS.EXPERIENCE,
            Component: (
              <EmploymentHistoryComponent
                employmentHistory={JSON.parse(
                  JSON.stringify(resumeData?.employmentHistory),
                )}
                responsiveMode={responsiveMode}
                osMode={true}
              />
            ),
            Icon: <span>&#128188;</span>,
          },
        ],
        [
          OS_RESUME_WINDOWS.PROJECTS,
          {
            id: OS_RESUME_WINDOWS.PROJECTS,
            title: OS_RESUME_WINDOWS.PROJECTS,
            Component: (
              <ProjectsComponent
                projects={JSON.parse(JSON.stringify(resumeData?.projects))}
                responsiveMode={responsiveMode}
                osMode={true}
              />
            ),
            Icon: <span>&#128187;</span>,
          },
        ],
        [
          OS_RESUME_WINDOWS.EDUCATION,
          {
            id: OS_RESUME_WINDOWS.EDUCATION,
            title: OS_RESUME_WINDOWS.EDUCATION,
            Component: (
              <EducationComponent
                education={resumeData?.education}
                responsiveMode={responsiveMode}
                osMode={true}
              />
            ),
            Icon: <span>&#128217;</span>,
          },
        ],
        [
          OS_RESUME_WINDOWS.CERTIFICATIONS,
          {
            id: OS_RESUME_WINDOWS.CERTIFICATIONS,
            title: OS_RESUME_WINDOWS.CERTIFICATIONS,
            Component: (
              <CertificationsComponent
                certifications={resumeData?.certifications}
                responsiveMode={responsiveMode}
                osMode={true}
              />
            ),
            Icon: <span>&#128220;</span>,
          },
        ],
        [
          OS_RESUME_WINDOWS.ASK_ME,
          {
            id: OS_RESUME_WINDOWS.ASK_ME,
            title: OS_RESUME_WINDOWS.ASK_ME,
            Component: (
              <AiAssistant responsiveMode={responsiveMode} osMode={true} />
            ),
            Icon: <span>&#128161;</span>,
            width: '300px',
          },
        ],
        [
          OS_RESUME_WINDOWS.AI_CHAT_BOT,
          {
            id: OS_RESUME_WINDOWS.AI_CHAT_BOT,
            title: OS_RESUME_WINDOWS.AI_CHAT_BOT,
            Component: <AIChatBot responsiveMode={responsiveMode} />,
            Icon: <span>&#129302;</span>,
            height: '320px',
          },
        ],
        [
          OS_RESUME_WINDOWS.RESUME_PDF,
          {
            id: OS_RESUME_WINDOWS.RESUME_PDF,
            title: OS_RESUME_WINDOWS.RESUME_PDF,
            Component: (
              <ResumePDF responsiveMode={responsiveMode} osMode={true} />
            ),
            Icon: <span>&#128221;</span>,
            height: '320px',
            isOverflowHidden: true,
          },
        ],
        [
          OS_RESUME_WINDOWS.JS_COMPILER,
          {
            id: OS_RESUME_WINDOWS.JS_COMPILER,
            title: OS_RESUME_WINDOWS.JS_COMPILER,
            Component: <JSCompiler />,
            Icon: <span>&#x2699;</span>,
            height: '320px',
          },
        ],
        [
          OS_RESUME_WINDOWS.SHARE,
          {
            id: OS_RESUME_WINDOWS.SHARE,
            title: OS_RESUME_WINDOWS.SHARE,
            Component: <Share />,
            Icon: <span>&#128228;</span>,
          },
        ],
        [
          OS_RESUME_WINDOWS.SHUTDOWN,
          {
            id: OS_RESUME_WINDOWS.SHUTDOWN,
            title: OS_RESUME_WINDOWS.SHUTDOWN,
            Component: <div />,
            Icon: <span>&#10060;</span>,
          },
        ],
      ]),
    [responsiveMode, resumeData],
  );

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 2000);
    return () => {
      clearTimeout(timer);
    };
  }, []);

  if (loading) {
    return <WindowsXPSplash />;
  }

  return (
    <>
      <WindowsXPDesktop
        responsiveMode={responsiveMode}
        availableWindowsModals={availableWindowsModals}
      />
    </>
  );
};

export default OSResume;
